// AI generated code
import * as THREE from 'three';

const SELECTOR = '[data-webien-night-sky]';

class NightSky {
    constructor(root) {
        this.root = root;
        this.canvasTarget = root.querySelector('[data-night-sky-canvas]');
        this.content = root.querySelector('[data-night-sky-content]');
        this.title = root.querySelector('[data-night-sky-title]');
        this.pointer = new THREE.Vector2(0, 0);
        this.pointerTarget = new THREE.Vector2(0, 0);
        this.startTime = performance.now();
        this.frame = null;

        if (!this.canvasTarget || !this.content || !this.title) {
            return;
        }

        this.setupScene();
        this.createStars();
        this.createMoon();
        this.bindEvents();
        this.resize();
        this.animate();
    }

    setupScene() {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 140);
        this.camera.position.z = 34;

        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
        });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.canvasTarget.appendChild(this.renderer.domElement);
    }

    createStars() {
        const count = 1400;
        const positions = new Float32Array(count * 3);
        const colors = new Float32Array(count * 3);
        const sizes = new Float32Array(count);
        const colorA = new THREE.Color('#cfe8ff');
        const colorB = new THREE.Color('#ffe9b3');
        const colorC = new THREE.Color('#ffffff');

        for (let i = 0; i < count; i += 1) {
            const radius = 18 + Math.random() * 58;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos((Math.random() * 2) - 1);
            const index = i * 3;
            const color = Math.random() > 0.82 ? colorB : (Math.random() > 0.45 ? colorA : colorC);

            positions[index] = radius * Math.sin(phi) * Math.cos(theta);
            positions[index + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[index + 2] = -Math.abs(radius * Math.cos(phi)) - Math.random() * 20;

            colors[index] = color.r;
            colors[index + 1] = color.g;
            colors[index + 2] = color.b;
            sizes[i] = Math.random() * 0.22 + 0.04;
        }

        this.starGeometry = new THREE.BufferGeometry();
        this.starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.starGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        this.starGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

        this.stars = new THREE.Points(
            this.starGeometry,
            new THREE.PointsMaterial({
                size: 0.12,
                vertexColors: true,
                transparent: true,
                opacity: 0.95,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
            })
        );
        this.scene.add(this.stars);

        const dustGeometry = new THREE.BufferGeometry();
        const dustPositions = new Float32Array(260 * 3);

        for (let i = 0; i < 260; i += 1) {
            const index = i * 3;
            dustPositions[index] = (Math.random() - 0.5) * 70;
            dustPositions[index + 1] = (Math.random() - 0.45) * 34;
            dustPositions[index + 2] = -28 - Math.random() * 46;
        }

        dustGeometry.setAttribute('position', new THREE.BufferAttribute(dustPositions, 3));
        this.dust = new THREE.Points(
            dustGeometry,
            new THREE.PointsMaterial({
                color: '#63f7df',
                size: 0.42,
                transparent: true,
                opacity: 0.12,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
            })
        );
        this.scene.add(this.dust);
    }

    createMoon() {
        const moon = new THREE.Mesh(
            new THREE.SphereGeometry(3.2, 48, 48),
            new THREE.MeshBasicMaterial({ color: '#f4f7ff' })
        );
        moon.position.set(12, 7, -12);
        this.scene.add(moon);
        this.moon = moon;

        const glow = new THREE.Mesh(
            new THREE.SphereGeometry(4.8, 48, 48),
            new THREE.MeshBasicMaterial({
                color: '#9ccfff',
                transparent: true,
                opacity: 0.14,
                blending: THREE.AdditiveBlending,
            })
        );
        glow.position.copy(moon.position);
        this.scene.add(glow);
        this.moonGlow = glow;
    }

    bindEvents() {
        this.onPointerMove = (event) => {
            const rect = this.root.getBoundingClientRect();
            this.pointerTarget.x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
            this.pointerTarget.y = ((event.clientY - rect.top) / rect.height - 0.5) * -2;
        };

        this.onPointerLeave = () => {
            this.pointerTarget.set(0, 0);
        };

        this.onResize = () => this.resize();

        this.root.addEventListener('pointermove', this.onPointerMove);
        this.root.addEventListener('pointerleave', this.onPointerLeave);
        window.addEventListener('resize', this.onResize);
    }

    resize() {
        const width = this.root.clientWidth || window.innerWidth;
        const height = this.root.clientHeight || window.innerHeight;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height, false);
    }

    animate() {
        const elapsed = (performance.now() - this.startTime) / 1000;
        this.pointer.lerp(this.pointerTarget, 0.055);

        this.stars.rotation.x = this.pointer.y * 0.08 + elapsed * 0.006;
        this.stars.rotation.y = this.pointer.x * 0.12 + elapsed * 0.012;
        this.dust.rotation.y = this.pointer.x * 0.18 + elapsed * 0.018;
        this.dust.rotation.x = this.pointer.y * 0.08;
        this.moon.position.x = 12 + this.pointer.x * 1.6;
        this.moon.position.y = 7 + this.pointer.y * 1.2;
        this.moonGlow.position.copy(this.moon.position);

        const textX = this.pointer.x * 28;
        const textY = this.pointer.y * -18;
        this.content.style.transform = `translate3d(${textX}px, ${textY}px, 0)`;
        this.title.style.textShadow = `${this.pointer.x * -18}px ${this.pointer.y * 14}px 34px rgba(99, 247, 223, 0.55), 0 0 42px rgba(255, 255, 255, 0.25)`;

        this.renderer.render(this.scene, this.camera);
        this.frame = window.requestAnimationFrame(() => this.animate());
    }

    destroy() {
        window.cancelAnimationFrame(this.frame);
        window.removeEventListener('resize', this.onResize);
        this.root.removeEventListener('pointermove', this.onPointerMove);
        this.root.removeEventListener('pointerleave', this.onPointerLeave);
        this.starGeometry?.dispose();
        this.stars?.material?.dispose();
        this.dust?.geometry?.dispose();
        this.dust?.material?.dispose();
        this.moon?.geometry?.dispose();
        this.moon?.material?.dispose();
        this.moonGlow?.geometry?.dispose();
        this.moonGlow?.material?.dispose();
        this.renderer?.dispose();
        this.renderer?.domElement?.remove();
    }
}

const initNightSkies = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
        if (root.nightSky) {
            root.nightSky.destroy();
        }

        root.nightSky = new NightSky(root);
    });
};

document.addEventListener('DOMContentLoaded', () => initNightSkies());

if (window.elementorFrontend?.hooks) {
    window.elementorFrontend.hooks.addAction('frontend/element_ready/webien_night_sky.default', ($scope) => {
        initNightSkies($scope[0] || document);
    });
}
